import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  useCreateOwnershipIndividualMutation,
  useCreateOwnershipMutation,
  useCreateOwnershipCompanyMutation,
  useUpdateOwnershipIndividualMutation,
  useCreateOwnershipCompanyDetailsMutation,
  useUpdateOwnershipCompanyDetailsMutation,
  useCreateOwnershipCompanyDetailsDraftMutation,
  useCreateOwnershipIndividualDraftMutation,
  useUpdateOwnershipIndividualDraftMutation,
  useUpdateOwnershipCompanyDetailsDraftMutation,
  useCreateCompanyMutation,
} from "../../../../store/Company/companyApiSlice";
import { getCountryCode } from "../../../../utils/countriesState";
import { needsCompanyRecord, ownershipSavePath, validateOwner } from "./ownershipFields";
import { resultName } from "./ownershipSearch";

/* The ownership dialog's behaviour. */
export const useOwnershipForm = ({ editing, total, selectedCompany, onSaved }) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [countries] = useState(() => getCountryCode());
  const [type, setType] = useState(selectedCompany ? "company" : "individual");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [share, setShare] = useState("");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const [createIndividual] = useCreateOwnershipIndividualMutation();
  const [createOwnershipRow] = useCreateOwnershipMutation();
  const [linkOwnershipToCompany] = useCreateOwnershipCompanyMutation();
  const [updateIndividual] = useUpdateOwnershipIndividualMutation();
  const [createCompanyDetails] = useCreateOwnershipCompanyDetailsMutation();
  const [updateCompanyDetails] = useUpdateOwnershipCompanyDetailsMutation();
  const [createCompanyDetailsDraft] = useCreateOwnershipCompanyDetailsDraftMutation();
  const [createIndividualDraft] = useCreateOwnershipIndividualDraftMutation();
  const [updateIndividualDraft] = useUpdateOwnershipIndividualDraftMutation();
  const [updateCompanyDetailsDraft] = useUpdateOwnershipCompanyDetailsDraftMutation();
  const [createCompany] = useCreateCompanyMutation();

  /* Transcribed: an existing owner seeds every field, and a company picked
     from the search seeds the name and country. */
  useEffect(() => {
    if (editing) {
      setType(editing.type || "");
      setCountry(editing.nationality || "");
      setName(editing.name || editing.company_name || "");
      setShare(
        editing.percentage_of_shares_held === undefined ||
          editing.percentage_of_shares_held === null
          ? ""
          : String(editing.percentage_of_shares_held)
      );
      return;
    }
    if (selectedCompany) {
      setType("company");
      /* resultName, not `.name`: a WorldBox hit carries `name` while a company
         already on TREVEX carries `registered_company_name`. Reading only the
         first would submit an empty company name for the second. */
      setName(resultName(selectedCompany));
      setCountry(selectedCompany.country || selectedCompany.country_code || "");
    }
  }, [editing, selectedCompany]);

  /* Transcribed: changing type clears the country, because the two lists are
     not interchangeable — a company's country comes from its record. */
  const onTypeChange = (next) => {
    setType(next);
    setCountry("");
  };

  /* Transcribed: the country control is fixed once something else has decided
     it — a company chosen from the search, or an existing owner. */
  const countryLocked = Boolean(
    editing?.nationality || selectedCompany?.country || selectedCompany?.country_code
  );

  /* Transcribed: the type cannot be changed on an existing owner. */
  const typeLocked = Boolean(editing);

  const submit = async () => {
    const found = validateOwner({
      type,
      name,
      companyName: name,
      country,
      share,
      total,
      editing,
    });
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const path = ownershipSavePath({
      verifiedCompany: currentEditingCompany?.verifiedCompany,
      editing,
      type,
    });

    /* Transcribed: a WorldBox-only company is created first so the ownership
       row can point at a real companyID. */
    const resolveOwnershipCompany = async () => {
      if (!needsCompanyRecord(selectedCompany)) return selectedCompany?.companyID || "";
      const created = await createCompany({
        data: {
          wb_win: selectedCompany.win,
          registered_company_name: selectedCompany.name,
          country_code: selectedCompany.country,
        },
        token,
      }).unwrap();
      return created?.data?.companyID;
    };

    setBusy(true);
    try {
      if (path === "updateIndDraft") {
        await updateIndividualDraft({
          data: {
            nationality: country,
            type,
            percentage_of_shares_held: share,
            name,
            ownershipCompany: "",
            current_companyID: companyID,
            request_type: "ownership individual draft created",
            /* Transcribed: only carried when the draft amends a live row. */
            ...(editing?.ownership_indID_id != null
              ? { ownership_indID: editing?.ownership_indID }
              : {}),
          },
          ownershipdraftID: editing?.ownership_ind_draftID,
          token,
        }).unwrap();
      } else if (path === "updateCompDraft") {
        await updateCompanyDetailsDraft({
          data: {
            nationality: country,
            country,
            state: selectedCompany?.state,
            type,
            percentage_of_shares_held: share,
            company_name: name,
            ownershipCompany: "",
            current_companyID: companyID,
            companyID,
            request_type: "Ownership Company Draft Created",
            ...(editing?.ownership_compID_id != null
              ? { ownership_compID: editing?.ownership_compID }
              : {}),
          },
          ownershipdraftID: editing?.ownership_comp_draftID,
          token,
        }).unwrap();
      } else if (path === "createIndDraft") {
        await createIndividualDraft({
          data: {
            nationality: country,
            type,
            percentage_of_shares_held: share,
            name,
            ownershipCompany: "",
            current_companyID: companyID,
            request_type: "ownership individual draft created",
            ...(editing?.ownership_indID ? { ownership_indID: editing.ownership_indID } : {}),
          },
          token,
        }).unwrap();
      } else if (path === "createCompDraft") {
        const ownershipCompanyID = await resolveOwnershipCompany();
        await createCompanyDetailsDraft({
          data: {
            nationality: country,
            country,
            state: selectedCompany?.state,
            type,
            percentage_of_shares_held: share,
            company_name: name,
            ownershipCompany: ownershipCompanyID,
            current_companyID: companyID,
            companyID,
            request_type: "Ownership Company Draft Created",
            ...(editing?.ownership_compID ? { ownership_compID: editing.ownership_compID } : {}),
          },
          token,
        }).unwrap();
      } else if (path === "updateInd") {
        await updateIndividual({
          data: {
            nationality: country,
            type,
            percentage_of_shares_held: share,
            name,
            ownershipCompany: "",
          },
          ownership_indId: editing?.ownership_indID,
          token,
        }).unwrap();
      } else if (path === "updateComp") {
        await updateCompanyDetails({
          data: {
            nationality: country,
            type,
            percentage_of_shares_held: share,
            /* Transcribed: the live update sends the record's OWN stored name
               and ownershipCompany rather than the form's, because the company
               field is read-only when editing. */
            company_name: editing?.company_name,
            ownershipCompany: editing?.ownershipCompany,
          },
          ownership_compId: editing?.ownership_compID,
          token,
        }).unwrap();
      } else {
        /* The three-call create chain. */
        let res;
        if (path === "createInd") {
          res = await createIndividual({
            data: {
              nationality: country,
              type,
              percentage_of_shares_held: share,
              name,
              ownershipCompany: "",
            },
            token,
          }).unwrap();
        } else {
          const ownershipCompanyID = await resolveOwnershipCompany();
          res = await createCompanyDetails({
            data: {
              nationality: country,
              state: selectedCompany?.state,
              country,
              type,
              percentage_of_shares_held: share,
              company_name: name,
              ownershipCompany: ownershipCompanyID,
            },
            token,
          }).unwrap();
        }

        /* Transcribed guard: the row is only created for an owner that came
           back with an id. */
        if (res?.ownership_indID || res?.ownership_compID) {
          const row = await createOwnershipRow({
            data: {
              ownership_indID: res?.ownership_indID,
              ownership_compID: res?.ownership_compID,
            },
            token,
          }).unwrap();

          if (row?.ownershipID) {
            await linkOwnershipToCompany({
              data: { ownershipID: row.ownershipID, companyID },
              token,
            }).unwrap();
          }
        }
      }

      onSaved?.();
    } catch (error) {
      /* The live code swallows every one of these in an empty catch, so a
         failed save closed the dialog as though it had worked. Written down
         and the dialog stays open. See FINDINGS F-040. */
      console.error("Ownership save failed:", error);
      setErrors({ form: "Couldn't save this owner. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return {
    type,
    onTypeChange,
    typeLocked,
    name,
    setName,
    country,
    setCountry,
    countryLocked,
    countries,
    share,
    setShare,
    errors,
    busy,
    submit,
    savePath: ownershipSavePath({
      verifiedCompany: currentEditingCompany?.verifiedCompany,
      editing,
      type,
    }),
  };
};

export default useOwnershipForm;
